import React, { FC } from "react";
import { styled } from "@material-ui/styles";

import { AdvertiserType } from "../../../../types/advertiserTypes";

type Props = {
  advertiser: AdvertiserType;
  onArchive: () => void;
  onUnarchive: () => void;
};

const Card = styled("div")({
  background: "#FFFFFF",
  border: "1px solid #E5E7EB",
  borderRadius: 10,
  overflow: "hidden",
});

const CardHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  borderBottom: "1px solid #F3F4F6",
});

const ArchivedBadge = styled("span")({
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: 4,
  background: "#F3F4F6",
  color: "#6B7280",
});

const ActiveBadge = styled("span")({
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: 4,
  background: "#DCFCE7",
  color: "#166534",
});

const ArchiveButton = styled("button")({
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 6,
  border: "1px solid #FECACA",
  background: "#FFF2F2",
  color: "#B91C1C",
  cursor: "pointer",
  "&:hover": { background: "#FEE2E2" },
});

const UnarchiveButton = styled("button")({
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 6,
  border: "1px solid #E5E7EB",
  background: "#F9FAFB",
  color: "#374151",
  cursor: "pointer",
  "&:hover": { background: "#F3F4F6" },
});

const TableWrap = styled("div")({
  padding: 24,
  overflowX: "auto",
});

const Table = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
});

const Th = styled("th")({
  textAlign: "left",
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#6B7280",
  padding: "0 16px 12px 0",
  borderBottom: "1px solid #F3F4F6",
  whiteSpace: "nowrap",
});

const Td = styled("td")({
  fontSize: 13,
  color: "#111827",
  padding: "14px 16px 14px 0",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "top",
});

const DomainLink = styled("a")({
  color: "#2D8CFF",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
});

const AdvertiserInfoCard: FC<Props> = ({ advertiser, onArchive, onUnarchive }) => {
  const address = [
    advertiser.address_1 ? `Address 1: ${advertiser.address_1}.` : null,
    advertiser.address_2 ? `Address 2: ${advertiser.address_2}.` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card>
      <CardHeader>
        <div />
        {advertiser.archived ? (
          <UnarchiveButton type="button" onClick={onUnarchive}>
            Unarchive
          </UnarchiveButton>
        ) : (
          <ArchiveButton type="button" onClick={onArchive}>
            Archive
          </ArchiveButton>
        )}
      </CardHeader>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Created by</Th>
              <Th>Domain</Th>
              <Th>Address</Th>
              <Th>Phone Number</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>{advertiser.name}</Td>
              <Td>
                {advertiser.archived ? <ArchivedBadge>Archived</ArchivedBadge> : <ActiveBadge>Active</ActiveBadge>}
              </Td>
              <Td>{advertiser.createdBy}</Td>
              <Td>
                {advertiser.domain ? (
                  <DomainLink href={advertiser.domain} target="_blank" rel="noopener noreferrer">
                    {advertiser.domain}
                  </DomainLink>
                ) : (
                  "—"
                )}
              </Td>
              <Td>{address || "—"}</Td>
              <Td>{advertiser.phone || "—"}</Td>
            </tr>
          </tbody>
        </Table>
      </TableWrap>
    </Card>
  );
};

export default AdvertiserInfoCard;
